import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemAvatar, 
  Avatar, 
  useTheme, 
  useMediaQuery, 
  Link, 
  Button,
  Fade,
  Paper,
  Divider,
  alpha,
  Container,
  Chip,
  IconButton,
  Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { News, PopularNewsSite, NewsType } from '../types';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import newsService from '../services/newsService';
import popularNewsSitesService from '../services/popularNewsSitesService';
import { useApiError } from '../hooks/useApiError';
import { ApiErrorResponse } from '../types/errors';
import LoadingDisplay from '../components/common/LoadingDisplay';
import ErrorDisplay from '../components/common/ErrorDisplay';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { handleError } = useApiError();
  const currentLanguage = i18n.language;
  const isInitialMount = useRef(true);
  const languageRef = useRef(currentLanguage);
  const scrollPositionRef = useRef<number>(0);
  const [isVisible, setIsVisible] = useState(false);

  // State for data
  const [latestNews, setLatestNews] = useState<News[]>([]);
  const [rumorNews, setRumorNews] = useState<News[]>([]);
  const [confirmedNews, setConfirmedNews] = useState<News[]>([]);
  const [popularSites, setPopularSites] = useState<PopularNewsSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchLatestNews = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
        scrollPositionRef.current = window.scrollY;
      }

      const newsResponse = await newsService.getLatestNews(pageNum);
      const newNews = newsResponse.results;
      setHasMore(newNews.length === 10);
      
      if (append) {
        setLatestNews(prev => [...prev, ...newNews]);
      } else {
        setLatestNews(newNews);
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      const apiError: ApiErrorResponse = {
        message: err instanceof Error ? err.message : 'Unknown error',
        code: 'UNKNOWN',
        originalError: err
      };
      handleError(apiError);
      setError(t('errors.fetchDataFailed'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [handleError, t]);

  const fetchNewsByType = useCallback(async (type: NewsType, pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
        scrollPositionRef.current = window.scrollY;
      }

      const newsResponse = await newsService.getNewsByType(type, pageNum);
      const newNews = newsResponse.results;
      setHasMore(newNews.length === 10);
      
      switch (type) {
        case 'RUMOR':
          if (append) {
            setRumorNews(prev => [...prev, ...newNews.filter(item => item.news_type === 'RUMOR')]);
          } else {
            setRumorNews(newNews.filter(item => item.news_type === 'RUMOR'));
          }
          break;
        case 'CONFIRMED':
          if (append) {
            setConfirmedNews(prev => [...prev, ...newNews.filter(item => item.news_type === 'CONFIRMED')]);
          } else {
            setConfirmedNews(newNews.filter(item => item.news_type === 'CONFIRMED'));
          }
          break;
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      const apiError: ApiErrorResponse = {
        message: err instanceof Error ? err.message : 'Unknown error',
        code: 'UNKNOWN',
        originalError: err
      };
      handleError(apiError);
      setError(t('errors.fetchDataFailed'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [handleError, t]);

  const fetchPopularSites = useCallback(async () => {
    try {
      const sitesResponse = await popularNewsSitesService.getPopularNewsSites(languageRef.current);
      setPopularSites(sitesResponse);
    } catch (err) {
      console.error('Error fetching popular sites:', err);
      const apiError: ApiErrorResponse = {
        message: err instanceof Error ? err.message : 'Unknown error',
        code: 'UNKNOWN',
        originalError: err
      };
      handleError(apiError);
    }
  }, [handleError]);

  const fetchData = useCallback(async () => {
    await Promise.all([
      fetchLatestNews(),
      fetchNewsByType('RUMOR'),
      fetchNewsByType('CONFIRMED'),
      fetchPopularSites()
    ]);
  }, [fetchLatestNews, fetchNewsByType, fetchPopularSites]);

  // Initial data fetch
  useEffect(() => {
    if (isInitialMount.current) {
      fetchData();
      isInitialMount.current = false;
    }
  }, [fetchData]);

  // Update language ref and fetch data when language changes
  useEffect(() => {
    if (languageRef.current !== currentLanguage) {
      languageRef.current = currentLanguage;
      if (!isInitialMount.current) {
        setPage(1);
        fetchData();
      }
    }
  }, [currentLanguage, fetchData]);

  // Effect to restore scroll position after loading more items
  useEffect(() => {
    if (!loadingMore && page > 1) {
      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: 'auto'
      });
    }
  }, [loadingMore, page]);

  // Set visibility after initial load
  useEffect(() => {
    if (!loading) {
      setIsVisible(true);
    }
  }, [loading]);

  const handleLoadMore = (type: NewsType | 'LATEST') => {
    const nextPage = page + 1;
    setPage(nextPage);
    if (type === 'LATEST') {
      fetchLatestNews(nextPage, true);
    } else {
      fetchNewsByType(type, nextPage, true);
    }
  };

  if (loading && page === 1) {
    return <LoadingDisplay />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  const NewsCard = ({ newsItem, featured = false }: { newsItem: News, featured?: boolean }) => {
    const [isBookmarked, setIsBookmarked] = useState(false);

    return (
      <Card 
        sx={{ 
          mb: 1,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8]
          },
          height: featured ? '100%' : 'auto'
        }}
      >
        <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
          <Box 
            sx={{ 
              mb: 2,
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: theme.shadows[2]
            }}
          >
            <img
              src={newsItem.image || ''}
              alt={newsItem.title}
              style={{
                width: '100%',
                height: featured ? 400 : 240,
                objectFit: 'cover'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                display: 'flex',
                gap: 1
              }}
            >
              <IconButton
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                }}
                title={t('news.share')}
              >
                <ShareIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setIsBookmarked(!isBookmarked)}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                }}
                title={isBookmarked ? t('news.unbookmark') : t('news.bookmark')}
              >
                {isBookmarked ? <BookmarkIcon color="primary" /> : <BookmarkBorderIcon />}
              </IconButton>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip 
              label={newsItem.news_type === 'RUMOR' ? t('news.rumor') : t('news.confirmed')}
              color={newsItem.news_type === 'RUMOR' ? 'warning' : 'success'}
              size="small"
              sx={{
                backgroundColor: newsItem.news_type === 'RUMOR' 
                  ? alpha(theme.palette.warning.main, 0.1)
                  : alpha(theme.palette.success.main, 0.1),
                '& .MuiChip-label': {
                  color: newsItem.news_type === 'RUMOR' 
                    ? theme.palette.warning.dark
                    : theme.palette.success.dark,
                  fontWeight: 500
                }
              }}
            />
            <Chip 
              label={new Date(newsItem.created_at).toLocaleDateString()}
              variant="outlined"
              size="small"
            />
          </Stack>
          <Typography 
            variant={featured ? "h4" : "h5"}
            component="h3" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 2,
              lineHeight: 1.3
            }}
          >
            {newsItem.title}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            paragraph
            sx={{ 
              mb: 3,
              lineHeight: 1.7
            }}
          >
            {newsItem.content}
          </Typography>
          <Link
            href={newsItem.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {t('home.readMore')} →
          </Link>
        </CardContent>
      </Card>
    );
  };

  const SectionTitle = ({ title }: { title: string }) => (
    <Typography
      variant={isMobile ? "h5" : "h4"}
      component="h2"
      gutterBottom
      sx={{
        mb: { xs: 3, sm: 4 },
        fontWeight: 600,
        color: 'primary.main',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -8,
          left: 0,
          width: 60,
          height: 3,
          backgroundColor: 'primary.main',
          borderRadius: 1
        }
      }}
    >
      {title}
    </Typography>
  );

  const LoadMoreButton = ({ onClick, loading }: { onClick: () => void, loading: boolean }) => (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 4
      }}
    >
      <Button
        variant="contained"
        onClick={onClick}
        disabled={loading}
        sx={{ 
          minWidth: 200,
          py: 1.5,
          px: 4,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '1.1rem',
          fontWeight: 500,
          boxShadow: theme.shadows[4],
          '&:hover': {
            boxShadow: theme.shadows[8]
          }
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                border: '2px solid',
                borderColor: 'background.paper',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
            {t('common.loading')}
          </Box>
        ) : (
          t('common.showMore')
        )}
      </Button>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <SEO
        title={t('header.title')}
        description={t('header.description')}
        keywords={t('home.keywords')}
      />
      <StructuredData
        type="WebSite"
        news={latestNews}
      />

      <Fade in={isVisible} timeout={500}>
        <Box>
          {/* Hero Section */}
          {latestNews.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={8}>
                  <NewsCard newsItem={latestNews[0]} featured={true} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    {latestNews.slice(1, 3).map((newsItem) => (
                      <NewsCard key={newsItem.id} newsItem={newsItem} />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Latest News and Popular Sites Section */}
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} md={8}>
              {latestNews.length > 3 && (
                <section aria-label={t('home.latestNews')}>
                  <SectionTitle title={t('home.latestNews')} />
                  <Grid container spacing={1}>
                    {latestNews.slice(3).map((newsItem) => (
                      <Grid item xs={12} sm={6} key={newsItem.id}>
                        <NewsCard newsItem={newsItem} />
                      </Grid>
                    ))}
                  </Grid>
                  {hasMore && (
                    <LoadMoreButton 
                      onClick={() => handleLoadMore('LATEST')} 
                      loading={loadingMore} 
                    />
                  )}
                </section>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {popularSites.length > 0 && (
                <aside>
                  <SectionTitle title={t('home.popularSites')} />
                  <Paper 
                    elevation={3}
                    sx={{ 
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        boxShadow: theme.shadows[8]
                      },
                      position: 'sticky',
                      top: 24
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                      <List>
                        {popularSites.map((site: PopularNewsSite, index: number) => (
                          <React.Fragment key={site.id}>
                            <ListItem 
                              disablePadding
                              sx={{ mb: 1 }}
                            >
                              <ListItemButton
                                component="a"
                                href={site.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  borderRadius: 2,
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    transform: 'translateX(8px)'
                                  }
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    src={site.logo_url}
                                    alt={site.name}
                                    sx={{
                                      width: { xs: 40, sm: 48 },
                                      height: { xs: 40, sm: 48 },
                                      border: '2px solid',
                                      borderColor: 'primary.main',
                                      backgroundColor: 'background.paper'
                                    }}
                                  />
                                </ListItemAvatar>
                                <Box sx={{ ml: 2 }}>
                                  <Typography 
                                    variant="subtitle1" 
                                    component="div"
                                    sx={{ 
                                      fontWeight: 500,
                                      color: 'text.primary'
                                    }}
                                  >
                                    {site.name}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {site.country}
                                  </Typography>
                                </Box>
                              </ListItemButton>
                            </ListItem>
                            {index < popularSites.length - 1 && (
                              <Divider sx={{ my: 1 }} />
                            )}
                          </React.Fragment>
                        ))}
                      </List>
                    </CardContent>
                  </Paper>
                </aside>
              )}
            </Grid>
          </Grid>

          {/* Transfer Rumors Section */}
          {rumorNews.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionTitle title={t('home.transferRumors')} />
              <Grid container spacing={1}>
                {rumorNews.map((newsItem) => (
                  <Grid item xs={12} sm={6} md={4} key={newsItem.id}>
                    <NewsCard newsItem={newsItem} />
                  </Grid>
                ))}
              </Grid>
              {hasMore && (
                <LoadMoreButton 
                  onClick={() => handleLoadMore('RUMOR')} 
                  loading={loadingMore} 
                />
              )}
            </Box>
          )}

          {/* Confirmed Transfers Section */}
          {confirmedNews.length > 0 && (
            <Box>
              <SectionTitle title={t('home.confirmedTransfers')} />
              <Grid container spacing={1}>
                {confirmedNews.map((newsItem) => (
                  <Grid item xs={12} sm={6} md={4} key={newsItem.id}>
                    <NewsCard newsItem={newsItem} />
                  </Grid>
                ))}
              </Grid>
              {hasMore && (
                <LoadMoreButton 
                  onClick={() => handleLoadMore('CONFIRMED')} 
                  loading={loadingMore} 
                />
              )}
            </Box>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default Home;
